"use client";

import { Api } from "@/app/api";
import type {
	CreateSupportTicketBody,
	SupportTicket,
	SupportTicketCategory,
	SupportTicketCollection,
	SupportTicketStatus,
} from "@/types";

export const supportTicketInjection = Api.injectEndpoints({
	endpoints: (builder) => ({
		getSupportTickets: builder.query<
			SupportTicketCollection,
			{
				page?: number;
				limit?: number;
				status?: SupportTicketStatus;
				category?: SupportTicketCategory;
				userId?: string;
				orderId?: string;
				search?: string;
				archived?: boolean;
				sortBy?: string;
			}
		>({
			query: ({ page = 1, limit = 10, status, category, userId, orderId, search, archived, sortBy }) => ({
				url: "/support-tickets",
				params: { page, limit, status, category, userId, orderId, search, archived, sortBy },
				headers: { "Content-Type": "application/json" },
			}),
			providesTags: (result) =>
				result
					? [
							...result.docs.map(({ _id }) => ({
								type: "support-tickets" as const,
								id: _id,
							})),
							{ type: "support-tickets", id: "LIST" },
						]
					: [{ type: "support-tickets", id: "LIST" }],
		}),
		getSupportTicket: builder.query<
			SupportTicket,
			string | { id: string; viewAs?: "user" | "staff" }
		>({
			query: (arg) => {
				const id = typeof arg === "string" ? arg : arg.id;
				const viewAs = typeof arg === "string" ? undefined : arg.viewAs;
				return {
					url: `/support-tickets/${id}`,
					params: { viewAs },
					headers: { "Content-Type": "application/json" },
				};
			},
			async onQueryStarted(arg, { dispatch, queryFulfilled, getState }) {
				const id = typeof arg === "string" ? arg : arg.id;
				try {
					const { data: viewedTicket } = await queryFulfilled;
					const affectedQueries = supportTicketInjection.util.selectInvalidatedBy(
						getState(),
						[{ type: "support-tickets", id }],
					);
					affectedQueries.forEach((query) => {
						if (query.endpointName !== "getSupportTickets" || !query.originalArgs) return;
						if (typeof query.originalArgs === "symbol") return;
						dispatch(
							supportTicketInjection.util.updateQueryData(
								"getSupportTickets",
								query.originalArgs,
								(draft) => {
									const target = draft.docs.find((ticket) => ticket._id === id);
									if (!target) return;
									target.userLastViewedAt = viewedTicket.userLastViewedAt;
									target.staffLastViewedAt = viewedTicket.staffLastViewedAt;
								},
							),
						);
					});
				} catch {
					// If the ticket fails to load, leave list caches untouched.
				}
			},
			providesTags: (_, __, arg) => [
				{ type: "support-tickets", id: typeof arg === "string" ? arg : arg.id },
			],
		}),
		createSupportTicket: builder.mutation<SupportTicket, CreateSupportTicketBody>({
			query: (body) => ({
				url: "/support-tickets",
				method: "POST",
				body,
				headers: { "Content-Type": "application/json" },
			}),
			invalidatesTags: [{ type: "support-tickets", id: "LIST" }],
		}),
		replySupportTicket: builder.mutation<
			SupportTicket,
			{ id: string; message: string; fileUrls?: string[]; isStaffReply?: boolean }
		>({
			query: ({ id, message, fileUrls, isStaffReply }) => ({
				url: `/support-tickets/${id}/replies`,
				method: "POST",
				body: { message, fileUrls, isStaffReply },
				headers: { "Content-Type": "application/json" },
			}),
			invalidatesTags: (_, __, { id }) => [
				{ type: "support-tickets", id },
				{ type: "support-tickets", id: "LIST" },
			],
		}),
		updateSupportTicketStatus: builder.mutation<
			SupportTicket,
			{ id: string; status: SupportTicketStatus }
		>({
			query: ({ id, status }) => ({
				url: `/support-tickets/${id}/status`,
				method: "PATCH",
				body: { status },
				headers: { "Content-Type": "application/json" },
			}),
			invalidatesTags: (_, __, { id }) => [
				{ type: "support-tickets", id },
				{ type: "support-tickets", id: "LIST" },
			],
		}),
		markSupportTicketUnread: builder.mutation<
			SupportTicket,
			{ id: string; viewAs?: "user" | "staff" }
		>({
			query: ({ id, viewAs }) => ({
				url: `/support-tickets/${id}/unread`,
				method: "PATCH",
				params: { viewAs },
				headers: { "Content-Type": "application/json" },
			}),
			invalidatesTags: (_, __, { id }) => [
				{ type: "support-tickets", id },
				{ type: "support-tickets", id: "LIST" },
			],
		}),
		archiveSupportTicket: builder.mutation<{ message: string }, string>({
			query: (id) => ({
				url: `/support-tickets/${id}/archive`,
				method: "PATCH",
				headers: { "Content-Type": "application/json" },
			}),
			invalidatesTags: (_, __, id) => [
				{ type: "support-tickets", id },
				{ type: "support-tickets", id: "LIST" },
			],
		}),
	}),
});

export const {
	useGetSupportTicketsQuery,
	useGetSupportTicketQuery,
	useCreateSupportTicketMutation,
	useReplySupportTicketMutation,
	useUpdateSupportTicketStatusMutation,
	useMarkSupportTicketUnreadMutation,
	useArchiveSupportTicketMutation,
} = supportTicketInjection;
